/**
 * CreatorProfileDrawer — the thread view's inline creator-profile panel
 * (Figma: the thread column shrinks to make room, no overlay). Sections
 * follow Figma's order: header card, Audience, Outreach, Country and time
 * zone (not in Figma; owner ruling 2026-10-11), Contact details, Rates, Files
 * received, Actions.
 *
 * Skipped against Figma, all for lack of backing data (not oversights):
 * - A niche/category tag on the header card — `OutreachRow` has no such field.
 * - "Follow-ups: N sent" — no sent-count field, only `nextFollowupAt`.
 * - Contact details' prev/next browser through discovered addresses (Figma's
 *   "Manage") — every address is listed at once instead, with its source and
 *   a radio for the primary (ContactEditPopover).
 * - Per-file "Applied"/"Needs review" status badges — `AttachmentSummary`
 *   carries no status field.
 * - The Rates section's per-deliverable, agent-assisted lock/unlock grid —
 *   the BE's `AgreeRate` is one atomic call for a whole rates array, no
 *   per-deliverable lock/unlock, no agent auto-lock, no reversibility. A
 *   different feature that doesn't exist yet, not a small gap like the
 *   others — kept at today's agree-once fidelity instead of faking it.
 */
import { useState } from 'react';
import { followupPauseAction } from './followupPauseUi';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { OutreachContactPatch, OutreachRow } from '@discovr/types';
import { isWithBrandStatus } from '@discovr/types';
import { fetchOutreachAttachments, downloadOutreachAttachment } from '@discovr/features';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import {
  Lock,
  FilePdf,
  FileImage,
  FileXls,
  FileDoc,
  FileText,
  DownloadSimple,
  ChartBar,
  PaperPlaneTilt,
  AddressBook,
  Receipt,
  ArrowLineUp,
  Play,
  Clock,
  MagnifyingGlass,
  X,
} from '../../../design-system/icons';
import { fmtDate } from '../../utils/campaignDetailHelpers';
import { useNow } from '../../hooks';
import { avatarInitials } from './avatarInitials';
import { ContactEditPopover } from './ContactEditPopover';
import { CreatorGeoSection } from './CreatorGeoSection';
import { InMessageTag } from './InMessageTag';
import { fmtAgreedItem, fmtAgreedTotal } from './agreedMoney';
import { formatBytes } from './attachmentFormat';
import gmailLogo from '../../../assets/connectors/gmail.png';
import { canAgreeRate, canMoveToBrand, hasRateToHandOff } from './handoffStates';
import { isSequenceEnded, nextFollowupDate, overdueLabel, SEQUENCE_ENDED_LABEL } from './rowStateLabels';
import { CreatorLookupBadge } from '../CreatorsTab/CreatorLookupBadge';
import { resolveCreatorLookupBadge } from '../CreatorsTab/creatorLookupStatus';
import { resolveMetricsCaption, TONE_CLASS } from '../CreatorsTab/creatorMetricsStatus';
import { contentMetricsCopy } from '../CreatorsTab/contentMetricsCopy';

export interface CreatorProfileDrawerProps {
  open: boolean;
  row: OutreachRow | null;
  canWrite: boolean;
  campaignZone?: string;
  /** The campaign's Automatic follow-ups switch (default ON); OFF leaves "Next follow-up" empty. */
  autoFollowups?: boolean;
  onSave: (patch: OutreachContactPatch) => Promise<void>;
  onClose: () => void;
  onAgreeRate?: () => void;
  onSendToBrand?: () => void;
  onPauseFollowups?: () => void;
  onResumeFollowups?: () => void;
}

const SECTION_LABEL = 'flex items-center gap-1.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase';
const SECTION_CARD = 'space-y-3 rounded-xl border border-border p-3';
// Same cadence as the Creators tab's own metrics-caption clock (CreatorsTab.tsx's useNow call).
const METRICS_CAPTION_TICK_MS = 300_000;

function AudienceStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border p-2.5">
      <p className="text-[10px] font-medium tracking-wide text-muted-foreground uppercase">{label}</p>
      <p className="text-body-sm-medium text-foreground">{value}</p>
    </div>
  );
}


export function CreatorProfileDrawer({
  open,
  row,
  canWrite,
  campaignZone,
  autoFollowups = true,
  onSave,
  onClose,
  onAgreeRate,
  onSendToBrand,
  onPauseFollowups,
  onResumeFollowups,
}: CreatorProfileDrawerProps) {
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);

  // A different creator clears any unlock-confirm left open from the
  // previous one (e.g. ThreadHeader's Prev/Next swaps `row` in place while
  // the drawer stays mounted) — same render-phase reset pattern as
  // ThreadView's composer seed.
  const [prevEntryId, setPrevEntryId] = useState(row?.entryId);
  if (row?.entryId !== prevEntryId) {
    setPrevEntryId(row?.entryId);
    setUnlockConfirmOpen(false);
  }

  const now = useNow(METRICS_CAPTION_TICK_MS);
  if (!open || !row) return null;

  const nextFollowupOn = nextFollowupDate(row, autoFollowups);
  const nextFollowupOverdue = nextFollowupOn ? overdueLabel(nextFollowupOn, new Date(now)) : null;
  const isAgreed = hasRateToHandOff(row) && !!row.agreedDeliverables?.length;
  const withBrand = isWithBrandStatus(row.status);
  const hasLockedRate = (hasRateToHandOff(row) || withBrand) && !!row.agreedDeliverables?.length;
  // BE sends no currency today (always undefined here, defaulting to INR) —
  // but if that ever changes, summing raw numbers across different
  // currencies into one figure would be a real wrong-amount bug, so this
  // guards it rather than assuming it can't happen.
  const singleCurrency =
    isAgreed && row.agreedDeliverables
      ? new Set(row.agreedDeliverables.map((d) => d.currency ?? 'INR')).size === 1
      : false;
  const lockedAmount =
    isAgreed && singleCurrency && row.agreedDeliverables
      ? fmtAgreedTotal(row.agreedDeliverables)
      : null;
  const pauseAction = followupPauseAction(row);
  const paused = pauseAction === 'resume';
  const canSendToBrand = canWrite && canMoveToBrand(row);
  const canPauseFollowups = canWrite && pauseAction !== null;
  const lookupBadge = resolveCreatorLookupBadge(row.lookupStatus, { isAdmin: true });
  // avg_views/engagement_rate and metrics_pending_since matter here, not just
  // metrics_status: without them a manual-equivalent legacy row would read as
  // unfetched, and a stalled pending row would read "Fetching…" forever
  // instead of "Fetch stalled" — exactly the caption the Creators tab shows.
  const metricsCaption = row.metricsStatus
    ? resolveMetricsCaption(
        {
          metrics_status: row.metricsStatus,
          avg_views: row.avgViews ?? 0,
          engagement_rate: row.engagementRate ?? 0,
          metrics_pending_since: row.metricsPendingSince,
        },
        row.lookupStatus,
        now,
        contentMetricsCopy(row.platform),
      )
    : null;

  return (
    // min-h-0 lets this flex item shrink below its content's natural height —
    // without it the flexbox default (min-height: auto) blocks the body's
    // overflow-y-auto from ever kicking in, and the panel just grows taller
    // than its row instead of scrolling internally. Header/body split (a
    // shrink-0 header + a flex-1 overflow-y-auto body) matches the insights
    // drawer's DrawerHeader + scrollable-content convention. `h-full` so the
    // panel takes its host's height whether that host is the thread row or a
    // Sheet column — without it a Sheet host has no height to bound the
    // body's scroll.
    <aside className="flex size-full min-h-0 max-w-sm min-w-0 flex-col border-l border-border pl-4">
      <div className="flex shrink-0 items-center justify-between pb-3">
        <h2 className="text-body-sm-medium text-foreground">Creator profile</h2>
        {/* Matches the Script/Content review drawers' close-button convention
            (see insights/DrawerHeader.tsx). */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          title="Close"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-accent"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-4">
      <div className="flex items-center gap-3">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-muted text-body-sm-medium text-muted-foreground">
          {row.avatarUrl ? (
            <img src={row.avatarUrl} alt="" className="size-12 rounded-full object-cover" />
          ) : (
            avatarInitials(row.displayName || row.username)
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-[17px] font-medium text-foreground">{row.displayName || row.username}</p>
          <p className="truncate text-body-sm-regular text-muted-foreground">@{row.username}</p>
          {(lookupBadge.pill || metricsCaption) && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <CreatorLookupBadge pill={lookupBadge.pill} />
              {metricsCaption && <span className={`text-[11px] ${TONE_CLASS[metricsCaption.tone]}`}>{metricsCaption.label}</span>}
            </div>
          )}
        </div>
      </div>

      {isAgreed && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
            <Lock size={11} /> Rate locked
          </span>
          {lockedAmount && (
            <span className="whitespace-nowrap rounded-full bg-(--stone-100) px-2.5 py-1 text-[11px] font-medium text-foreground">
              {lockedAmount} locked
            </span>
          )}
        </div>
      )}

      <div className={SECTION_CARD}>
        <span className={SECTION_LABEL}>
          <ChartBar size={13} /> Audience
        </span>
        <div className="grid grid-cols-3 gap-2">
          {row.followersHidden && !row.followers
            ? <AudienceStat label="Followers" value="Hidden" />
            : row.followers != null && <AudienceStat label="Followers" value={row.followers.toLocaleString()} />}
          {row.avgViews != null && <AudienceStat label="Avg views" value={row.avgViews.toLocaleString()} />}
          {row.engagementRate != null && <AudienceStat label="ER" value={`${row.engagementRate.toFixed(2)}%`} />}
        </div>
      </div>

      <div className={SECTION_CARD}>
        <span className={SECTION_LABEL}>
          <PaperPlaneTilt size={13} /> Outreach
        </span>
        <dl className="space-y-1.5 text-body-sm-regular">
          <div className="flex items-center justify-between">
            <dt className="text-muted-foreground">Reached on</dt>
            <dd><img src={gmailLogo} alt="Email" className="h-3.5 w-auto" /></dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-muted-foreground">Last sent</dt>
            <dd className="text-foreground">{row.lastMessage?.sentAt ? fmtDate(row.lastMessage.sentAt) : '—'}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-muted-foreground">Next follow-up</dt>
            <dd className="text-foreground">{paused ? 'Paused' : nextFollowupOn ? (nextFollowupOverdue ? `${fmtDate(nextFollowupOn)} · ${nextFollowupOverdue}` : fmtDate(nextFollowupOn)) : isSequenceEnded(row) ? SEQUENCE_ENDED_LABEL : '—'}</dd>
          </div>
        </dl>
      </div>

      <div className={SECTION_CARD}>
        <CreatorGeoSection userId={row.userId} platform={row.platform} campaignZone={campaignZone} />
      </div>

      <div className={SECTION_CARD}>
        <span className={SECTION_LABEL}>
          <AddressBook size={13} /> Contact details
        </span>
        {canWrite && <ContactEditPopover row={row} onSave={onSave} onClose={onClose} />}
      </div>

      <div className={SECTION_CARD}>
        <div className="flex items-center justify-between gap-2">
          <span className={SECTION_LABEL}>
            <Receipt size={13} /> Rates
          </span>
          {hasLockedRate && (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              {withBrand ? 'Held by the brand' : `${row.agreedDeliverables?.length} locked`}
            </span>
          )}
        </div>
        {hasLockedRate ? (
          <div className="space-y-2">
            <ul className="divide-y divide-border">
              {row.agreedDeliverables?.map((d, index) => (
                <li key={`${d.type}-${index}`} className="flex items-center justify-between gap-2 py-2.5 text-body-sm-regular">
                  <span className="capitalize text-foreground">{d.type.replace(/_/g, ' ')}</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-medium tabular-nums text-emerald-700">
                    <Lock size={11} aria-hidden="true" />
                    <span className="sr-only">Locked at</span>
                    {fmtAgreedItem(d)}
                  </span>
                </li>
              ))}
            </ul>
            {isAgreed && (
              <Button type="button" variant="outline" size="sm" onClick={() => setUnlockConfirmOpen(true)}>
                Unlock rate
              </Button>
            )}
            <p className="text-[12px] text-muted-foreground">
              {withBrand ? 'With the brand · Re-agree from the thread to revise' : 'Locked from the outreach agreement · Re-agree from the thread to change'}
            </p>
          </div>
        ) : canWrite && canAgreeRate(row) ? (
          <div className="space-y-2">
            <p className="text-[12px] text-muted-foreground">For rates agreed by call or WhatsApp, outside the email thread.</p>
            <Button type="button" variant="outline" size="sm" onClick={() => onAgreeRate?.()}>
              Add rates
            </Button>
          </div>
        ) : null}
      </div>

      <FilesReceivedSection entryId={row.entryId} />

      {(canSendToBrand || canPauseFollowups) && (
        <div className={SECTION_CARD}>
          <span className={SECTION_LABEL}>Actions</span>
          <div className="flex flex-col items-start gap-2">
            {canSendToBrand && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-body-sm-regular"
                disabled={!hasRateToHandOff(row)}
                title={!hasRateToHandOff(row) ? 'Agree a rate first' : undefined}
                onClick={() => onSendToBrand?.()}
              >
                <ArrowLineUp size={14} />
                Move to brand dashboard
              </Button>
            )}
            {canPauseFollowups && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-body-sm-regular"
                onClick={() => (paused ? onResumeFollowups?.() : onPauseFollowups?.())}
              >
                {paused ? <Play size={14} /> : <Clock size={14} />}
                {paused ? 'Resume follow-ups' : 'Pause follow-ups'}
              </Button>
            )}
          </div>
        </div>
      )}
      </div>

      <Dialog open={unlockConfirmOpen} onOpenChange={setUnlockConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlock this rate?</DialogTitle>
            <DialogDescription>
              {row.displayName || row.username}
              {lockedAmount ? ` · ${lockedAmount} locked` : ''}. The creator is not notified — this only changes what
              we hold on file.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setUnlockConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                setUnlockConfirmOpen(false);
                onAgreeRate?.();
              }}
            >
              Unlock
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </aside>
  );
}

function FilesReceivedSection({ entryId }: { entryId: string }) {
  const [seeAllOpen, setSeeAllOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['outreach-attachments', entryId],
    queryFn: () => fetchOutreachAttachments(entryId),
  });
  const download = useMutation({
    mutationFn: ({ id, filename }: { id: number; filename: string }) => downloadOutreachAttachment(entryId, id, filename),
    onError: () => toast.error("Couldn't download this file — try again"),
  });

  const filtered = (data ?? []).filter((a) => a.filename.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className={SECTION_CARD}>
      <div className="flex items-center justify-between">
        <span className={SECTION_LABEL}>
          <FileText size={13} /> Files received
        </span>
        <div className="flex items-center gap-2">
          {!!data?.length && <span className="text-caption-regular text-muted-foreground">{data.length}</span>}
          {!!data?.length && (
            <button type="button" className="text-caption-medium text-primary hover:underline" onClick={() => setSeeAllOpen(true)}>
              See all
            </button>
          )}
        </div>
      </div>
      {isLoading ? (
        <p className="text-body-sm-regular text-muted-foreground">Loading…</p>
      ) : !data?.length ? (
        <p className="text-body-sm-regular text-muted-foreground">No files received yet</p>
      ) : (
        <ul className="space-y-2">
          {data.slice(0, 2).map((a) => (
            <FileRow key={a.id} filename={a.filename} contentType={a.contentType} sizeBytes={a.sizeBytes} receivedAt={a.receivedAt} isInline={a.isInline} onDownload={() => download.mutate({ id: a.id, filename: a.filename })} disabled={download.isPending} />
          ))}
        </ul>
      )}

      <Dialog open={seeAllOpen} onOpenChange={setSeeAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Files received</DialogTitle>
            <DialogDescription>Everything this creator has sent on this campaign.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute top-1/2 left-2.5 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search files" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-8" />
          </div>
          <ul className="space-y-2">
            {filtered.map((a) => (
              <FileRow key={a.id} filename={a.filename} contentType={a.contentType} sizeBytes={a.sizeBytes} receivedAt={a.receivedAt} isInline={a.isInline} onDownload={() => download.mutate({ id: a.id, filename: a.filename })} disabled={download.isPending} />
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FileRow({
  filename,
  contentType,
  sizeBytes,
  receivedAt,
  isInline,
  onDownload,
  disabled,
}: {
  filename: string;
  contentType: string;
  sizeBytes: number;
  receivedAt: string;
  isInline?: boolean;
  onDownload: () => void;
  disabled: boolean;
}) {
  return (
    <li className="flex items-center gap-2 text-body-sm-regular">
      <FileTypeIcon contentType={contentType} />
      <span className="flex min-w-0 flex-1 items-center gap-1.5">
        <span className="truncate">{filename}</span>
        {isInline ? <InMessageTag /> : null}
      </span>
      <span className="text-[12px] text-muted-foreground">{formatBytes(sizeBytes)}</span>
      <span className="text-[12px] text-muted-foreground">{formatShortDate(receivedAt)}</span>
      <button type="button" aria-label={`Download ${filename}`} className="text-muted-foreground hover:text-foreground" disabled={disabled} onClick={onDownload}>
        <DownloadSimple size={15} />
      </button>
    </li>
  );
}

function FileTypeIcon({ contentType }: { contentType: string }) {
  if (contentType === 'application/pdf') return <FilePdf size={16} />;
  if (contentType.startsWith('image/')) return <FileImage size={16} />;
  if (contentType.includes('spreadsheet') || contentType === 'text/csv') return <FileXls size={16} />;
  if (contentType.includes('word')) return <FileDoc size={16} />;
  return <FileText size={16} />;
}

function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short' }).format(new Date(iso));
}
